import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import api from '../../../domain/api';
import { normalizeFacebookAdAccount } from '../../../shared/hooks/UseFacebookAccount';

const META_ACCOUNT_VIEWS_STORAGE_KEY = 'meta-ads-account-saved-views-v1';

const fetchAccounts = async () => {
  const res = await api.get('/facebook-ads/ad-accounts');
  return (res.data.adAccounts || []).map(normalizeFacebookAdAccount);
};

const createViewId = () =>
  `view-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const readSavedViews = () => {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const rawValue = window.localStorage.getItem(
      META_ACCOUNT_VIEWS_STORAGE_KEY,
    );
    const parsed = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const FacebookAdsAccountsReport = () => {
  const history = useHistory();
  const [filters, setFilters] = useState({
    accountName: '',
    businessName: '',
    status: '',
    currency: '',
  });
  const [selectedAccountIds, setSelectedAccountIds] = useState([]);
  const [selectedOnlyScopeIds, setSelectedOnlyScopeIds] = useState([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [savedViews, setSavedViews] = useState(readSavedViews);
  const [selectedViewId, setSelectedViewId] = useState('');
  const [viewName, setViewName] = useState('');

  const {
    data: accounts = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['facebook-ads-accounts', 'api-v3'],
    queryFn: fetchAccounts,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(
      META_ACCOUNT_VIEWS_STORAGE_KEY,
      JSON.stringify(savedViews),
    );
  }, [savedViews]);

  useEffect(() => {
    if (!accounts.length) {
      return;
    }

    const availableAccountIds = new Set(
      accounts.map((account) => String(account.accountId)),
    );

    setSelectedAccountIds((current) =>
      current.filter((accountId) => availableAccountIds.has(String(accountId))),
    );
    setSelectedOnlyScopeIds((current) =>
      current.filter((accountId) => availableAccountIds.has(String(accountId))),
    );

    setSavedViews((current) =>
      current
        .map((view) => ({
          ...view,
          accountIds: (view.accountIds || []).filter((accountId) =>
            availableAccountIds.has(String(accountId)),
          ),
        }))
        .filter((view) => view.accountIds.length > 0 || !view.showSelectedOnly),
    );
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    return accounts.filter((account) => {
      const matchesAccountName =
        !filters.accountName ||
        account.accountName
          ?.toLowerCase()
          .includes(filters.accountName.toLowerCase());

      const matchesBusinessName =
        !filters.businessName ||
        account.businessName
          ?.toLowerCase()
          .includes(filters.businessName.toLowerCase());

      const matchesStatus =
        !filters.status ||
        account.status?.toLowerCase() === filters.status.toLowerCase();

      const matchesCurrency =
        !filters.currency ||
        account.currency?.toLowerCase() === filters.currency.toLowerCase();

      const matchesSelection =
        !showSelectedOnly ||
        selectedOnlyScopeIds.includes(String(account.accountId));

      return (
        matchesAccountName &&
        matchesBusinessName &&
        matchesStatus &&
        matchesCurrency &&
        matchesSelection
      );
    });
  }, [accounts, filters, selectedAccountIds, showSelectedOnly]);

  const selectedCount = selectedAccountIds.length;
  const visibleAccountIds = filteredAccounts.map((account) =>
    String(account.accountId),
  );
  const allVisibleSelected =
    visibleAccountIds.length > 0 &&
    visibleAccountIds.every((accountId) =>
      selectedAccountIds.includes(accountId),
    );

  const statuses = useMemo(() => {
    return [
      ...new Set(accounts.map((account) => account.status).filter(Boolean)),
    ];
  }, [accounts]);

  const currencies = useMemo(() => {
    return [
      ...new Set(accounts.map((account) => account.currency).filter(Boolean)),
    ];
  }, [accounts]);

  const stats = useMemo(() => {
    return accounts.reduce(
      (summary, account) => {
        const normalizedStatus = String(account.status || '').toLowerCase();

        if (normalizedStatus === 'active') {
          summary.active += 1;
        } else if (normalizedStatus === 'disabled') {
          summary.disabled += 1;
        } else if (normalizedStatus === 'unsettled') {
          summary.unsettled += 1;
        } else {
          summary.other += 1;
        }

        return summary;
      },
      { active: 0, disabled: 0, unsettled: 0, other: 0 },
    );
  }, [accounts]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      accountName: '',
      businessName: '',
      status: '',
      currency: '',
    });
    setShowSelectedOnly(false);
    setSelectedOnlyScopeIds([]);
    setSelectedViewId('');
  };

  const openCampaigns = (accountId) => {
    const params = new URLSearchParams();
    params.set('tab', 'campaigns');
    params.set('accountId', accountId);
    history.push({
      pathname: '/facebook-ads-reports',
      search: params.toString(),
    });
  };

  const toggleAccountSelection = (accountId) => {
    const normalizedId = String(accountId);

    setSelectedAccountIds((current) =>
      current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId],
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedAccountIds((current) => {
      const currentSet = new Set(current);

      if (allVisibleSelected) {
        return current.filter(
          (accountId) => !visibleAccountIds.includes(accountId),
        );
      }

      visibleAccountIds.forEach((accountId) => currentSet.add(accountId));
      return Array.from(currentSet);
    });
  };

  const clearSelection = () => {
    setSelectedAccountIds([]);
    setSelectedOnlyScopeIds([]);
    setShowSelectedOnly(false);
    setSelectedViewId('');
  };

  const saveCurrentView = () => {
    const trimmedName = viewName.trim();

    if (!trimmedName) {
      return;
    }

    const nextView = {
      id: createViewId(),
      name: trimmedName,
      accountIds: [...selectedAccountIds],
      filters: { ...filters },
      showSelectedOnly: selectedAccountIds.length > 0 ? true : showSelectedOnly,
      createdAt: new Date().toISOString(),
    };

    setSavedViews((current) => {
      const withoutSameName = current.filter(
        (view) => view.name.toLowerCase() !== trimmedName.toLowerCase(),
      );
      return [...withoutSameName, nextView];
    });
    setSelectedViewId(nextView.id);
    setViewName('');
  };

  const applySavedView = (viewId) => {
    setSelectedViewId(viewId);

    if (!viewId) {
      return;
    }

    const matchedView = savedViews.find((view) => view.id === viewId);
    if (!matchedView) {
      return;
    }

    const nextSelectedIds = (matchedView.accountIds || []).map((accountId) =>
      String(accountId),
    );

    setFilters({
      accountName: matchedView.filters?.accountName || '',
      businessName: matchedView.filters?.businessName || '',
      status: matchedView.filters?.status || '',
      currency: matchedView.filters?.currency || '',
    });
    setSelectedAccountIds(nextSelectedIds);
    setSelectedOnlyScopeIds(nextSelectedIds);
    setShowSelectedOnly(
      nextSelectedIds.length > 0 ? true : Boolean(matchedView.showSelectedOnly),
    );
  };

  const deleteSavedView = () => {
    if (!selectedViewId) {
      return;
    }

    setSavedViews((current) =>
      current.filter((view) => view.id !== selectedViewId),
    );
    setSelectedViewId('');
  };

  return (
    <div className='meta-report-shell'>
      <div className='meta-report-header'>
        <div className='hero-copy'>
          <h1 className='meta-report-title'>
            <i className='fa fa-facebook-square' aria-hidden='true'></i>
            Meta Ads Accounts
          </h1>
          <p className='meta-report-subtitle'>
            Live account inventory from the Meta Ads API.
          </p>
        </div>

        <div className='meta-report-stats'>
          <div className='meta-report-stat'>
            <span className='meta-report-stat-label'>Accounts</span>
            <strong>{filteredAccounts.length}</strong>
          </div>
          <div className='meta-report-stat'>
            <span className='meta-report-stat-label'>Active</span>
            <strong>{stats.active}</strong>
          </div>
          <div className='meta-report-stat'>
            <span className='meta-report-stat-label'>Disabled</span>
            <strong>{stats.disabled}</strong>
          </div>
        </div>
      </div>

      <div className='report-filters-card'>
        <div className='filter-toolbar'>
          <div className='filter-summary-pills'>
            <span className='summary-pill active'>Active {stats.active}</span>
            <span className='summary-pill disabled'>
              Disabled {stats.disabled}
            </span>
            <span className='summary-pill neutral'>
              Unsettled {stats.unsettled}
            </span>
            {stats.other > 0 && (
              <span className='summary-pill neutral'>Other {stats.other}</span>
            )}
          </div>
        </div>

        <div className='saved-views-card'>
          <div className='saved-views-grid'>
            <div className='filter-group'>
              <label>Saved Views</label>
              <select
                value={selectedViewId}
                onChange={(event) => applySavedView(event.target.value)}
                className='filter-select'
              >
                <option value=''>Choose a saved view</option>
                {savedViews.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.name}
                  </option>
                ))}
              </select>
            </div>

            <div className='filter-group'>
              <label>Save Current View</label>
              <input
                type='text'
                value={viewName}
                onChange={(event) => setViewName(event.target.value)}
                placeholder='Example: Priority accounts'
                className='filter-input'
              />
            </div>

            <div className='saved-view-actions'>
              <button
                type='button'
                className='primary-button'
                onClick={saveCurrentView}
                disabled={!viewName.trim()}
              >
                Save View
              </button>
              <button
                type='button'
                className='secondary-button'
                onClick={deleteSavedView}
                disabled={!selectedViewId}
              >
                Delete View
              </button>
            </div>
          </div>

          <div className='selection-toolbar'>
            <div className='selection-summary'>
              <strong>{selectedCount}</strong> accounts selected
              {showSelectedOnly && (
                <span className='selection-badge'>Selected only</span>
              )}
            </div>
            <div className='selection-actions'>
              <label className='selection-toggle'>
                <input
                  type='checkbox'
                  checked={showSelectedOnly}
                  onChange={(event) => {
                    const isChecked = event.target.checked;
                    setShowSelectedOnly(isChecked);
                    setSelectedOnlyScopeIds(
                      isChecked ? [...selectedAccountIds] : [],
                    );
                  }}
                  disabled={selectedCount === 0}
                />
                <span>Show selected only</span>
              </label>
              <button
                type='button'
                className='secondary-button'
                onClick={clearSelection}
                disabled={selectedCount === 0}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>

        <div className='report-filters-grid'>
          <div className='filter-group'>
            <label>Account Name</label>
            <input
              type='text'
              value={filters.accountName}
              onChange={(event) =>
                handleFilterChange('accountName', event.target.value)
              }
              placeholder='Search account name'
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Business Name</label>
            <input
              type='text'
              value={filters.businessName}
              onChange={(event) =>
                handleFilterChange('businessName', event.target.value)
              }
              placeholder='Search business name'
              className='filter-input'
            />
          </div>

          <div className='filter-group'>
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(event) =>
                handleFilterChange('status', event.target.value)
              }
              className='filter-select'
            >
              <option value=''>All statuses</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className='filter-group'>
            <label>Currency</label>
            <select
              value={filters.currency}
              onChange={(event) =>
                handleFilterChange('currency', event.target.value)
              }
              className='filter-select'
            >
              <option value=''>All currencies</option>
              {currencies.map((currency) => (
                <option key={currency} value={currency}>
                  {currency}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='filter-actions-row'>
          <span className='filter-results-text'>
            Showing {filteredAccounts.length} of {accounts.length} API accounts
          </span>
          <button
            type='button'
            className='secondary-button'
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        </div>
      </div>

      <div className='report-table-card'>
        {isLoading ? (
          <div className='report-feedback'>
            <div className='loading-spinner'></div>
            <p>Loading accounts from Meta...</p>
          </div>
        ) : isError ? (
          <div className='report-feedback'>
            <i className='fa fa-exclamation-triangle' aria-hidden='true'></i>
            <p>{error?.message || 'Failed to fetch ad accounts'}</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className='report-feedback'>
            <i className='fa fa-search' aria-hidden='true'></i>
            <p>
              {accounts.length > 0
                ? 'No accounts match the current filters.'
                : 'No ad accounts were returned by the API.'}
            </p>
          </div>
        ) : (
          <div className='table-scroll'>
            <table className='report-table'>
              <thead>
                <tr>
                  <th className='selection-column'>
                    <input
                      type='checkbox'
                      checked={allVisibleSelected}
                      onChange={toggleVisibleSelection}
                      aria-label='Select visible accounts'
                    />
                  </th>
                  <th>Account</th>
                  <th>Account ID</th>
                  <th>Business</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Funding</th>
                  <th>Spent</th>
                  <th>Spend Cap</th>
                  <th>Available Funds</th>
                  <th>Balance</th>
                  <th>Currency</th>
                  <th>Time Zone</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => {
                  const rawFunds = parseFloat(
                    account.availableFunds ??
                      account.raw?.prepay_balance ??
                      account.availableBalance ??
                      NaN,
                  );
                  const isLowFunds =
                    Number.isFinite(rawFunds) && rawFunds < 2000;
                  return (
                    <tr
                      key={account.accountId}
                      style={
                        isLowFunds ? { backgroundColor: '#ffd6d6' } : undefined
                      }
                    >
                      <td className='selection-column'>
                        <input
                          type='checkbox'
                          checked={selectedAccountIds.includes(
                            String(account.accountId),
                          )}
                          onChange={() =>
                            toggleAccountSelection(account.accountId)
                          }
                          aria-label={`Select ${account.accountName}`}
                        />
                      </td>
                      <td className='sticky-column'>
                        <button
                          type='button'
                          className='table-link-button'
                          onClick={() => openCampaigns(account.accountId)}
                        >
                          {account.accountName}
                        </button>
                        <div className='table-subtext'>
                          {account.businessName ||
                            account.ownerName ||
                            'Meta account'}
                        </div>
                      </td>
                      <td className='mono-cell'>{account.accountId}</td>
                      <td>{account.businessName || 'N/A'}</td>
                      <td>{account.ownerName || 'N/A'}</td>
                      <td>
                        <span
                          className={`status-pill ${account.status?.toLowerCase().replace(/\s+/g, '-') || 'unknown'}`}
                        >
                          <i className='fa fa-circle' aria-hidden='true'></i>
                          {account.status || 'Unknown'}
                        </span>
                      </td>
                      <td>
                        <span className='meta-chip'>
                          {account.paymentType || 'Unknown'}
                        </span>
                        {account.fundingSourceDisplay && (
                          <div className='table-subtext'>
                            {account.fundingSourceDisplay}
                          </div>
                        )}
                      </td>
                      <td className='metric-cell'>
                        {account.amountSpentLabel ||
                          account.amountSpent ||
                          'N/A'}
                      </td>
                      <td className='metric-cell'>
                        {account.spendCapLabel || account.spendCap || 'N/A'}
                      </td>
                      <td className='metric-cell balance-cell'>
                        {account.availableFundsLabel ||
                          account.availableFunds ||
                          account.availableBalanceLabel ||
                          account.availableBalance ||
                          'N/A'}
                      </td>
                      <td className='metric-cell'>
                        {account.currentBalanceLabel ||
                          account.currentBalance ||
                          'N/A'}
                      </td>
                      <td>
                        <span className='meta-chip muted'>
                          {account.currency || 'N/A'}
                        </span>
                      </td>
                      <td>{account.timezoneName || 'N/A'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style jsx>{`
        .meta-report-shell {
          background: #f5f7fb;
          min-height: 100vh;
        }

        .meta-report-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 20px 22px;
          background:
            radial-gradient(
              circle at top right,
              rgba(255, 255, 255, 0.18),
              transparent 24%
            ),
            linear-gradient(135deg, #1877f2 0%, #155fbe 48%, #0f4ea8 100%);
          color: #fff;
          border-radius: 14px;
          margin-bottom: 18px;
        }

        .hero-copy {
          max-width: 620px;
        }

        .meta-report-title {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0;
          font-size: 24px;
          font-weight: 700;
        }

        .meta-report-subtitle {
          margin: 6px 0 0;
          color: rgba(255, 255, 255, 0.82);
          font-size: 13px;
        }

        .meta-report-stats {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .meta-report-stat {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          padding: 12px 14px;
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.14);
          min-width: 96px;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12);
        }

        .meta-report-stat-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: rgba(255, 255, 255, 0.8);
        }

        .meta-report-stat strong {
          font-size: 24px;
          line-height: 1.1;
        }

        .report-filters-card,
        .report-table-card {
          background: #fff;
          border: 1px solid #e5eaf3;
          border-radius: 14px;
          box-shadow: 0 8px 30px rgba(15, 23, 42, 0.05);
        }

        .report-filters-card {
          padding: 18px 20px;
          margin-bottom: 18px;
        }

        .saved-views-card {
          border: 1px solid #e5eaf3;
          border-radius: 14px;
          background: #fbfcfe;
          padding: 16px;
          margin-bottom: 16px;
        }

        .saved-views-grid {
          display: grid;
          grid-template-columns: minmax(220px, 1.1fr) minmax(240px, 1fr) auto;
          gap: 14px;
          align-items: end;
        }

        .saved-view-actions {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        .selection-toolbar {
          margin-top: 14px;
          padding-top: 14px;
          border-top: 1px solid #e5eaf3;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .selection-summary {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #334155;
          font-size: 14px;
        }

        .selection-summary strong {
          font-size: 18px;
          color: #0f172a;
        }

        .selection-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .selection-toggle {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #334155;
          font-size: 13px;
          font-weight: 600;
        }

        .selection-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 999px;
          background: #dbeafe;
          color: #1d4ed8;
          font-size: 12px;
          font-weight: 700;
        }

        .filter-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .filter-summary-pills {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .summary-pill,
        .meta-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 6px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }

        .summary-pill.active {
          background: #e8f7ed;
          color: #16753b;
        }

        .summary-pill.disabled {
          background: #fdecec;
          color: #b42318;
        }

        .summary-pill.neutral {
          background: #eef2f7;
          color: #475569;
        }

        .report-filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 14px;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .filter-group label {
          font-size: 12px;
          font-weight: 700;
          color: #556277;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .filter-input,
        .filter-select {
          height: 42px;
          border: 1px solid #d4dceb;
          border-radius: 10px;
          padding: 0 12px;
          font-size: 14px;
          color: #1f2937;
          background: #fbfcfe;
        }

        .filter-actions-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 16px;
        }

        .filter-results-text {
          font-size: 13px;
          color: #556277;
        }

        .secondary-button {
          border: 1px solid #cfd7e6;
          background: #f8fafc;
          color: #334155;
          border-radius: 10px;
          height: 38px;
          padding: 0 14px;
          font-weight: 600;
          cursor: pointer;
        }

        .primary-button {
          border: 1px solid #1877f2;
          background: #1877f2;
          color: #fff;
          border-radius: 10px;
          height: 38px;
          padding: 0 14px;
          font-weight: 600;
          cursor: pointer;
        }

        .primary-button:disabled,
        .secondary-button:disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }

        .report-feedback {
          min-height: 260px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: #5b6474;
        }

        .loading-spinner {
          width: 28px;
          height: 28px;
          border: 3px solid #dbe5f3;
          border-top-color: #1877f2;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .table-scroll {
          overflow-x: auto;
          border-radius: 14px;
        }

        .report-table {
          width: 100%;
          min-width: 1240px;
          border-collapse: collapse;
        }

        .report-table thead th {
          position: sticky;
          top: 0;
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
          color: #5b6474;
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-align: left;
          padding: 14px 16px;
          border-bottom: 1px solid #e5eaf3;
          z-index: 2;
        }

        .selection-column {
          width: 48px;
          min-width: 48px;
          text-align: center;
        }

        .selection-column input {
          width: 16px;
          height: 16px;
          accent-color: #1877f2;
          cursor: pointer;
        }

        .report-table tbody td {
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
          color: #1f2937;
          font-size: 14px;
          vertical-align: middle;
        }

        .report-table tbody tr:nth-child(even) {
          background: #fbfcfe;
        }

        .report-table tbody tr:hover {
          background: #f4f9ff;
        }

        .sticky-column {
          position: sticky;
          left: 0;
          background: inherit;
          z-index: 1;
          min-width: 220px;
          box-shadow: 1px 0 0 #eef2f7;
        }

        .table-link-button {
          border: none;
          background: none;
          padding: 0;
          color: #1663d6;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          text-align: left;
        }

        .table-link-button:hover {
          text-decoration: underline;
        }

        .table-subtext {
          margin-top: 4px;
          color: #64748b;
          font-size: 12px;
        }

        .mono-cell {
          font-family:
            'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
          color: #556277;
        }

        .metric-cell {
          font-weight: 700;
          color: #0f172a;
        }

        .balance-cell {
          color: #166534;
        }

        .status-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          background: #eef2f7;
          color: #475569;
          font-size: 12px;
          font-weight: 700;
        }

        .meta-chip {
          background: #eff6ff;
          color: #1d4ed8;
        }

        .meta-chip.muted {
          background: #eef2f7;
          color: #475569;
        }

        .status-pill.active {
          background: #eaf8ef;
          color: #16753b;
        }

        .status-pill.disabled,
        .status-pill.closed,
        .status-pill.rejected {
          background: #fdecec;
          color: #b42318;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .meta-report-header,
          .filter-actions-row,
          .filter-toolbar,
          .selection-toolbar {
            flex-direction: column;
            align-items: stretch;
          }

          .meta-report-stat {
            align-items: flex-start;
          }

          .meta-report-stats {
            justify-content: flex-start;
          }

          .saved-views-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default FacebookAdsAccountsReport;
